import { lazy } from "react"
import type { ComponentType } from "react"

/**
 * Enhanced lazy loading with automatic retry logic for chunk loading failures.
 *
 * Usage:
 * ```tsx
 * const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 * ```
 *
 * When a chunk fails to load:
 * 1. Retries the import with cache-busting parameter
 * 2. On continued failure, triggers a full page reload
 *
 * This handles cases where users have outdated cached index.html
 * referencing chunks that no longer exist after deployment.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFunction: () => Promise<{ default: T } | unknown>,
  retryCount = 3,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const executeImport = async (attempt = 1): Promise<{ default: T }> => {
      try {
        const result = await importFunction();
        if (result && typeof result === "object" && "default" in result) {
          return result as { default: T };
        }
        throw new Error("Module does not export default");
      } catch (error) {
        console.warn(`⚠️ Dynamic import failed (attempt ${attempt}/${retryCount}):`, error);

        // Check if it's a chunk loading error
        const isChunkError =
          error instanceof Error &&
          (error.message?.includes("Failed to fetch") ||
            error.message?.includes("ChunkLoadError") ||
            error.message?.includes("Loading chunk") ||
            error.message?.includes("dynamically imported module"));

        if (!isChunkError || attempt >= retryCount) {
          // Not a chunk error or max retries reached
          // Force a page reload to get fresh chunks
          console.error("🔄 Max retries reached. Reloading page...");

          // Add timestamp to bust cache
          const url = new URL(globalThis.location.href);
          url.searchParams.set("_reload", Date.now().toString());
          globalThis.location.href = url.toString();

          // Throw to prevent further execution
          throw error;
        }

        // Wait before retrying (exponential backoff)
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise((resolve) => setTimeout(resolve, delay));

        // Retry the import
        return executeImport(attempt + 1);
      }
    };

    return executeImport();
  });
}

/**
 * Preload a lazy component to improve perceived performance.
 * Call this when you know a component will be needed soon.
 *
 * Usage:
 * ```tsx
 * const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 *
 * // Preload when hovering over a navigation link
 * onMouseEnter={() => preloadLazy(MyComponent)}
 * ```
 */
export function preloadLazy<T extends ComponentType<unknown>>(LazyComponent: React.LazyExoticComponent<T>): void {
  // TypeScript hack to access the internal promise
  const lazyComponent = LazyComponent as unknown as Record<string, unknown>;

  if (lazyComponent._result === null) {
    // Component hasn't been loaded yet
    const constructor = lazyComponent._ctor as () => Promise<unknown>;
    constructor().catch((error: Error) => {
      console.warn("Failed to preload component:", error);
    });
  }
}
